<template>
	<div
		v-click-outside="resetEditMode"
		class="time-entry-done"
		:class="classAddons"
	>
		<div class="time-entry-done__bg">
			<TimeEntryDoneInput
				v-if="isEditMode"
				:state="timeEntryState"
				:default-time-entry-value="defaultTimeEntryValue"
				@add="onMarkDone($event)"
				@blur="onBlur"
				@keydown.esc="resetEditMode"
				@input-valid-change="onInputValidation"
			/>
		</div>

		<SuiButton
			v-tooltip="tooltip"
			:is-disabled="isDone || hasUncompletedSubtasks"
			variant="icon"
			class="time-entry-done__button"
			:aria-label="tooltip"
			@click="onMarkDone()"
		>
			<SuiIcon name="icon-check-24" />
		</SuiButton>
	</div>
</template>

<script setup lang="ts">
import {
	SuiIcon,
	SuiButton,
	clickOutside as vClickOutside,
} from '@developers/scoro-ui';
import TimeEntryDoneInput from './TimeEntryDoneInput.vue';
import { computed, ref, nextTick } from 'vue';
import { timeToSeconds } from '@/utils/time';
import { useTranslation } from '@/composables';
import { type ValueOf } from '@/types';
import {
	useTaskMissingRequiredFieldsLazyQuery,
	TaskMissingRequiredFieldsDocument,
} from '@/generated/graphql';
import { TIME_ENTRY_STATE } from '../types';
import {
	TIME_ENTRY_ANIMATION_DURATION,
	HIGHLIGHT_INPUT_ERROR_WAIT,
} from '../../constants';
import { wait } from '@/utils/misc';
import { useMessage } from '@/features/Message';

export type TimeEntryDoneProps = {
	defaultTimeEntryValue?: string;
	isDone?: boolean;
	allowChangeDuration?: boolean;
	taskId?: string;
	hasUncompletedSubtasks?: boolean;
};

const props = withDefaults(defineProps<TimeEntryDoneProps>(), {
	defaultTimeEntryValue: '0:00',
	taskId: undefined,
});

const emit = defineEmits<{
	(e: 'done', duration: number): void;
}>();

const { t } = useTranslation();
const { load, refetch, onResult, result } =
	useTaskMissingRequiredFieldsLazyQuery();
const { addErrorMessage } = useMessage();

const timeEntryState = ref<ValueOf<typeof TIME_ENTRY_STATE>>();
const isEditMode = ref(false);
const userValue = ref<number>(timeToSeconds(props.defaultTimeEntryValue));
const isInputValid = ref(true);
const isAnimating = ref(false);

const resetEditMode = async () => {
	isEditMode.value = false;
	await nextTick();
	isInputValid.value = true;
};
const durationInput = ref<number | undefined>(undefined);

const onBlur = (duration: number) => {
	if (!isInputValid.value) {
		return;
	}
	userValue.value = duration;
};

const markAsDone = (duration?: number) => {
	if (!isInputValid.value || isAnimating.value) {
		return;
	} else if (props.isDone) {
		isEditMode.value = false;
		return;
	}

	if (!isEditMode.value && props.allowChangeDuration) {
		isEditMode.value = true;
		return;
	}

	userValue.value = duration ?? userValue.value;

	if (userValue.value !== 0) {
		timeEntryState.value = TIME_ENTRY_STATE.SUCCESS;
		setTimeout(() => {
			isEditMode.value = false;
			timeEntryState.value = undefined;
		}, TIME_ENTRY_ANIMATION_DURATION);
	} else {
		isEditMode.value = false;
	}

	emit('done', userValue.value);
};

const onInputValidation = async (isValid: boolean) => {
	isInputValid.value = isValid;
	if (!isValid) {
		timeEntryState.value = TIME_ENTRY_STATE.ERROR;
		isAnimating.value = true;

		await wait(HIGHLIGHT_INPUT_ERROR_WAIT);
		await wait(TIME_ENTRY_ANIMATION_DURATION);
		isAnimating.value = false;
		timeEntryState.value = undefined;
	}
};
const validateTaskRequiredFields = () => {
	if (!result.value && props?.taskId) {
		load(
			TaskMissingRequiredFieldsDocument,
			{
				id: props.taskId,
			},
			{
				fetchPolicy: 'no-cache',
			}
		);
	} else {
		refetch();
	}
};

const onMarkDone = async (duration?: number) => {
	durationInput.value = duration;

	if (props?.taskId) {
		validateTaskRequiredFields();
	} else {
		markAsDone(duration);
	}
};

const classAddons = computed(() => ({
	'time-entry-done--edit-mode': isEditMode.value,
	'time-entry-done--is-done': props.isDone,
}));

const tooltip = computed(() => {
	if (props.hasUncompletedSubtasks) {
		return t('has_uncompleted_subtasks');
	}

	return props.isDone ? t('task_done') : t('setTaskDone');
});

onResult(async ({ data }) => {
	if (data?.Task?.isTimeEntriesRequiredFieldsMissing && !props.isDone) {
		addErrorMessage({
			message: t('task_done_time_entries_mandatory_fields_required'),
		});
	} else {
		markAsDone(durationInput.value);
	}
});
</script>

<style lang="scss">
.time-entry-done {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	position: relative;

	&__bg {
		display: none;
		position: absolute;
		width: 16rem;
		top: -1rem;
		right: 0;
		padding: 1rem 4rem 1rem 1rem;
		background-color: $neutral-00;
		box-shadow: $scoro-box-shadow-modal;
		align-items: center;
		border-radius: 1rem;
		z-index: 3;
	}

	&__button.time-entry-done__button {
		--sui-button-v-padding: 0.5rem;
		--sui-button-h-padding: 0.5rem;
		--sui-button-hover-background-color: #{$neutral-20};

		position: relative;
		z-index: 4;
	}

	&--edit-mode {
		.time-entry-done__bg {
			display: flex;
		}

		.time-entry-done__button {
			--sui-button-hover-background-color: transparent;
		}
	}
}
</style>
